import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaClock,
  FaFilter,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import Reveal from "../../components/Reveal/Reveal";
import { useUser } from "./UserContext";

import { getGroupsHome } from "../../services/groupServices";
import { getSettings } from "../../services/settingsServices";
import { getAdherentsByGroup } from "../../services/dashboardServices";
import { getDojos } from "../../services/dojoServices";

import DojoInfoCard from "./groupPlanning/DojoInfoCard";
import DojoPhotoModal from "./groupPlanning/DojoPhotoModal";
import GroupMiniCard from "./groupPlanning/GroupMiniCard";
import GroupModal from "./groupPlanning/GroupModal";
import PlanningChip from "./groupPlanning/PlanningChip";

import {
  cn,
  extractAnneesUnique,
  extractDisciplinesUnique,
  extractJoursFromGroupes,
  matchAnnee,
  normalizeDiscipline,
} from "./groupPlanning/groupPlanningUtils";

const GroupTableSkeleton = () => {
  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-48 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />

          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />

          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div
          className="
            mt-12 grid justify-center gap-5
            grid-cols-[repeat(auto-fit,minmax(280px,300px))]
          "
        >
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="h-72 w-full animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const EmptyGroupsState = () => {
  return (
    <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/70 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div
        className="
          mx-auto mb-4 flex h-14 w-14 items-center justify-center
          rounded-2xl bg-orange-100 text-orange-600
          dark:bg-orange-500/10 dark:text-orange-400
        "
      >
        <FaLayerGroup />
      </div>

      <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Aucun groupe trouvé
      </h3>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-300">
        Modifiez les filtres ou sélectionnez un autre dojo pour afficher les
        groupes disponibles.
      </p>
    </div>
  );
};

export default function GroupTable() {
  const { user } = useUser();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [saisonActive, setSaisonActive] = useState("");
  const [loading, setLoading] = useState(true);

  const [selectedDojoId, setSelectedDojoId] = useState(null);
  const [selectedAnnee, setSelectedAnnee] = useState("");
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [view, setView] = useState("groups");

  const [showMap, setShowMap] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [dojoPhotoOpen, setDojoPhotoOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const [dojoData, groupsData, adherentsData, settingsData] =
          await Promise.all([
            getDojos(),
            getGroupsHome(),
            getAdherentsByGroup(),
            getSettings(),
          ]);

        if (!mounted) return;

        const combinedGroups = (groupsData || []).map((group) => {
          const adherentInfo = (adherentsData || []).find(
            (item) => item.nom === group.nom
          );

          return {
            ...group,
            adherentCount: adherentInfo?.adherentCount || 0,
            quota_groupe:
              adherentInfo?.quota_groupe || group.quota_groupe || 0,
          };
        });

        const activeGroups = combinedGroups.filter((group) => {
          if (
            group?.is_active === undefined ||
            group?.is_active === null ||
            group?.is_active === ""
          ) {
            return true;
          }

          return (
            group.is_active === true ||
            group.is_active === 1 ||
            group.is_active === "1"
          );
        });

        const nextDojos = Array.isArray(dojoData) ? dojoData : [];

        setDojos(nextDojos);
        setGroups(Array.isArray(activeGroups) ? activeGroups : []);
        setSaisonActive(
          settingsData?.[0]?.saison_active ||
            settingsData?.saison_active ||
            ""
        );

        if (nextDojos.length) {
          setSelectedDojoId(nextDojos[0].id);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des groupes :", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const selectedDojo = useMemo(() => {
    return (
      dojos.find(
        (dojo) => String(dojo.id) === String(selectedDojoId)
      ) || null
    );
  }, [dojos, selectedDojoId]);

  const mapUrl = useMemo(() => {
    if (!selectedDojo?.dojo_adresse) return "";

    return `https://www.google.com/maps?q=${encodeURIComponent(
      selectedDojo.dojo_adresse
    )}&output=embed`;
  }, [selectedDojo?.dojo_adresse]);

  const listeAnnees = useMemo(() => {
    return extractAnneesUnique(groups);
  }, [groups]);

  const listeDisciplines = useMemo(() => {
    return extractDisciplinesUnique(groups);
  }, [groups]);

  const filteredGroups = useMemo(() => {
    let baseGroups = selectedDojoId
      ? groups.filter(
          (group) => String(group.dojo_id) === String(selectedDojoId)
        )
      : groups;

    if (selectedDiscipline) {
      baseGroups = baseGroups.filter(
        (group) =>
          normalizeDiscipline(group.discipline_groupe) ===
          normalizeDiscipline(selectedDiscipline)
      );
    }

    if (selectedAnnee) {
      baseGroups = baseGroups.filter((group) =>
        matchAnnee(group.annees_naissance, selectedAnnee)
      );
    }

    return baseGroups;
  }, [groups, selectedDojoId, selectedDiscipline, selectedAnnee]);

  const joursUtilises = useMemo(() => {
    return extractJoursFromGroupes(filteredGroups);
  }, [filteredGroups]);

  const getCoursParJour = (jour) => {
    const result = [];

    filteredGroups.forEach((group) => {
      for (let index = 1; index <= 6; index += 1) {
        const horaire = group[`horaire${index}`];

        if (horaire && String(horaire).startsWith(jour)) {
          const parts = String(horaire).split(" ");
          const plage = parts.slice(1).join(" ").trim();

          result.push({
            ...group,
            horaire: plage || horaire,
          });
        }
      }
    });

    return result.sort((a, b) =>
      String(a.horaire || "").localeCompare(
        String(b.horaire || ""),
        "fr"
      )
    );
  };

  const dojoNameOf = (dojoId) => {
    return (
      dojos.find((dojo) => String(dojo.id) === String(dojoId))
        ?.dojo_nom || ""
    );
  };

  const dojoForModal = useMemo(() => {
    if (!selectedGroup) return null;

    return (
      dojos.find(
        (dojo) => String(dojo.id) === String(selectedGroup.dojo_id)
      ) || selectedDojo
    );
  }, [selectedGroup, dojos, selectedDojo]);

  const resetFilters = () => {
    setSelectedDiscipline("");
    setSelectedAnnee("");
  };

  const handleJoinClick = () => {
    navigate(user ? "/ajouter-inscription-adherent" : "/register");
  };

  if (loading) {
    return <GroupTableSkeleton />;
  }

  return (
    <section
      id="groupes"
      className="
        relative scroll-mt-[calc(var(--site-navbar-h)+24px)]
        overflow-hidden bg-slate-50 py-20 text-slate-950
        dark:bg-slate-950 dark:text-white
        lg:py-28
      "
    >
      {/* Décor de fond */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <Reveal className="mx-auto max-w-3xl text-center">
          <div
            className="
              inline-flex items-center gap-2 rounded-full
              border border-orange-200 bg-orange-50
              px-4 py-2 text-xs font-bold uppercase
              tracking-[0.22em] text-orange-700 shadow-sm
              dark:border-orange-500/20 dark:bg-orange-500/10
              dark:text-orange-300
            "
          >
            <FaClock />
            Groupes & horaires
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Cours, groupes{" "}
            <span className="text-orange-600 dark:text-orange-400">
              et plannings
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Consultez les groupes disponibles, les dojos, les horaires, les
            tarifs et les informations utiles pour choisir le bon créneau.
          </p>

          {saisonActive && (
            <p
              className="
                mt-4 inline-flex rounded-full border border-slate-200
                bg-white/80 px-4 py-2 text-sm font-bold
                text-slate-600 shadow-sm
                dark:border-white/10 dark:bg-white/5 dark:text-slate-300
              "
            >
              Saison {saisonActive}
            </p>
          )}
        </Reveal>

        {/* Statistiques */}
        <Reveal
          delay={100}
          className="mx-auto mt-10 grid max-w-5xl gap-4 sm:grid-cols-3"
        >
          <div className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FaMapMarkerAlt className="mx-auto mb-3 text-2xl text-orange-600 dark:text-orange-400" />

            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {dojos.length}
            </p>

            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
              Dojo{dojos.length > 1 ? "s" : ""}
            </p>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FaUsers className="mx-auto mb-3 text-2xl text-orange-600 dark:text-orange-400" />

            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {groups.length}
            </p>

            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
              Groupe{groups.length > 1 ? "s" : ""}
            </p>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FaFilter className="mx-auto mb-3 text-2xl text-orange-600 dark:text-orange-400" />

            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {filteredGroups.length}
            </p>

            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
              Résultat{filteredGroups.length > 1 ? "s" : ""}
            </p>
          </div>
        </Reveal>

        {/* Sélection des dojos */}
        <Reveal delay={140}>
          <div className="mt-10 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => {
                setSelectedDojoId(null);
                setShowMap(false);
              }}
              className={cn(
                "rounded-full border px-4 py-2 text-sm font-bold transition",
                selectedDojoId === null
                  ? "border-orange-300 bg-orange-100 text-orange-800 shadow-sm dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-300"
                  : "border-slate-200 bg-white/80 text-slate-700 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:border-orange-500/30 dark:hover:bg-orange-500/10 dark:hover:text-orange-300"
              )}
            >
              Tous les dojos
            </button>

            {dojos.map((dojo) => (
              <button
                key={dojo.id}
                type="button"
                onClick={() => {
                  setSelectedDojoId(dojo.id);
                  setShowMap(false);
                }}
                className={cn(
                  "rounded-full border px-4 py-2 text-sm font-bold transition",
                  String(selectedDojoId) === String(dojo.id)
                    ? "border-orange-300 bg-orange-100 text-orange-800 shadow-sm dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-300"
                    : "border-slate-200 bg-white/80 text-slate-700 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:border-orange-500/30 dark:hover:bg-orange-500/10 dark:hover:text-orange-300"
                )}
              >
                {dojo.dojo_nom}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Informations du dojo */}
        {selectedDojoId && selectedDojo && (
          <Reveal variant="zoom" delay={160}>
            <DojoInfoCard
              dojo={selectedDojo}
              mapUrl={mapUrl}
              showMap={showMap}
              onToggleMap={() =>
                setShowMap((previous) => !previous)
              }
              onOpenPhoto={() => setDojoPhotoOpen(true)}
            />
          </Reveal>
        )}

        {/* Choix de la vue */}
        <Reveal delay={180}>
          <div className="mt-8 flex justify-center">
            <div
              className="
                inline-flex rounded-full border border-slate-200
                bg-white/80 p-1 shadow-sm backdrop-blur-xl
                dark:border-white/10 dark:bg-white/5
              "
            >
              <button
                type="button"
                onClick={() => setView("groups")}
                className={cn(
                  "rounded-full px-5 py-2.5 text-sm font-bold transition",
                  view === "groups"
                    ? "bg-orange-600 text-white shadow-md shadow-orange-600/25 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-400"
                    : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10"
                )}
              >
                Vue groupes
              </button>

              <button
                type="button"
                onClick={() => setView("planning")}
                className={cn(
                  "rounded-full px-5 py-2.5 text-sm font-bold transition",
                  view === "planning"
                    ? "bg-orange-600 text-white shadow-md shadow-orange-600/25 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-400"
                    : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10"
                )}
              >
                Vue planning
              </button>
            </div>
          </div>
        </Reveal>

        {/* Filtres */}
        <Reveal delay={200}>
          <div
            className="
              mx-auto mt-8 max-w-5xl rounded-[2rem]
              border border-slate-200 bg-white/85 p-5
              shadow-sm backdrop-blur-xl
              dark:border-white/10 dark:bg-slate-900/80
            "
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
                  Discipline
                </label>

                <select
                  className="
                    w-full rounded-2xl border border-slate-200
                    bg-white px-4 py-3 text-sm font-semibold
                    text-slate-700 outline-none transition
                    focus:border-orange-500 focus:ring-4
                    focus:ring-orange-500/10
                    dark:border-white/10 dark:bg-slate-950
                    dark:text-slate-200 dark:focus:border-orange-400
                  "
                  value={selectedDiscipline}
                  onChange={(event) =>
                    setSelectedDiscipline(event.target.value)
                  }
                >
                  <option value="">Toutes les disciplines</option>

                  {listeDisciplines.map((discipline) => (
                    <option key={discipline} value={discipline}>
                      {discipline}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
                  Année de naissance
                </label>

                <select
                  className="
                    w-full rounded-2xl border border-slate-200
                    bg-white px-4 py-3 text-sm font-semibold
                    text-slate-700 outline-none transition
                    focus:border-orange-500 focus:ring-4
                    focus:ring-orange-500/10
                    dark:border-white/10 dark:bg-slate-950
                    dark:text-slate-200 dark:focus:border-orange-400
                  "
                  value={selectedAnnee}
                  onChange={(event) =>
                    setSelectedAnnee(event.target.value)
                  }
                >
                  <option value="">Toutes les années</option>

                  {listeAnnees.map((annee) => (
                    <option key={annee} value={annee}>
                      {annee}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500 dark:text-slate-300">
              <span className="font-semibold">
                {filteredGroups.length} groupe(s) trouvé(s)
              </span>

              {(selectedDiscipline || selectedAnnee) && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="
                    rounded-full border border-slate-200 px-4 py-2
                    text-xs font-bold text-slate-600 transition
                    hover:border-orange-200 hover:bg-orange-50
                    hover:text-orange-700
                    dark:border-white/10 dark:text-slate-300
                    dark:hover:border-orange-500/30
                    dark:hover:bg-orange-500/10
                    dark:hover:text-orange-300
                  "
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          </div>
        </Reveal>

        {/* Vue groupes */}
        {view === "groups" && (
          <div className="mt-14">
            <Reveal className="mb-6 flex flex-col gap-3 text-center sm:items-center">
              <div>
                <h3 className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
                  Groupes disponibles
                </h3>

                <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">
                  Cliquez sur un groupe pour consulter les horaires, tarifs et
                  informations détaillées.
                </p>
              </div>
            </Reveal>

            {filteredGroups.length === 0 ? (
              <EmptyGroupsState />
            ) : (
              <div
                className="
                  grid justify-center gap-6
                  grid-cols-[repeat(auto-fit,minmax(280px,300px))]
                "
              >
                {filteredGroups.map((group, index) => (
                  <Reveal
                    key={group.id || `${group.nom}-${index}`}
                    variant="zoom"
                    delay={index * 60}
                    className="w-full"
                  >
                    <GroupMiniCard
                      group={group}
                      dojoName={dojoNameOf(group.dojo_id)}
                      onOpen={setSelectedGroup}
                    />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Vue planning */}
        {view === "planning" && (
          <div className="mt-14">
            <Reveal className="mb-6 flex flex-col gap-3 text-center sm:items-center">
              <div>
                <h3 className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
                  Planning des cours
                </h3>

                <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">
                  Retrouvez les cours classés par jour de pratique.
                </p>
              </div>
            </Reveal>

            {joursUtilises.length === 0 ? (
              <EmptyGroupsState />
            ) : (
              <div
                className="
                  grid justify-center gap-6
                  grid-cols-[repeat(auto-fit,minmax(280px,300px))]
                "
              >
                {joursUtilises.map((jour, dayIndex) => (
                  <Reveal
                    key={jour}
                    variant="zoom"
                    delay={dayIndex * 80}
                    className="w-full"
                  >
                    <div
                      className="
                        h-full rounded-[2rem]
                        border border-slate-200 bg-white/85
                        p-5 shadow-sm backdrop-blur-xl
                        transition duration-300
                        hover:-translate-y-1 hover:border-orange-300
                        hover:shadow-xl
                        dark:border-white/10 dark:bg-slate-900/80
                        dark:hover:border-orange-500/40
                      "
                    >
                      <div className="mb-5 flex items-center gap-3 border-b border-slate-200 pb-4 dark:border-white/10">
                        <span
                          className="
                            flex h-11 w-11 shrink-0 items-center
                            justify-center rounded-2xl
                            bg-orange-100 text-orange-600
                            dark:bg-orange-500/10 dark:text-orange-400
                          "
                        >
                          <FaCalendarAlt />
                        </span>

                        <h4 className="font-barlow text-2xl font-bold uppercase text-slate-950 dark:text-white">
                          {jour}
                        </h4>
                      </div>

                      <div className="space-y-3">
                        {getCoursParJour(jour).map((cours, index) => (
                          <PlanningChip
                            key={`${jour}-${cours.id}-${index}`}
                            cours={cours}
                            dojoName={dojoNameOf(cours.dojo_id)}
                            onOpen={setSelectedGroup}
                          />
                        ))}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Bouton inscription */}
        <Reveal delay={220}>
          <div className="mt-14 flex justify-center">
            <button
              type="button"
              onClick={handleJoinClick}
              className="
                group inline-flex items-center justify-center gap-3
                rounded-full bg-orange-600 px-7 py-4
                text-sm font-bold text-white
                shadow-[0_20px_60px_rgba(234,88,12,0.35)]
                transition duration-200
                hover:-translate-y-0.5 hover:bg-orange-700
                hover:shadow-[0_24px_70px_rgba(234,88,12,0.45)]
                focus:outline-none focus:ring-4 focus:ring-orange-500/25
                dark:bg-orange-500 dark:hover:bg-orange-400
              "
            >
              <FaUserPlus />

              {user
                ? "Faire une inscription ou cours d’essai"
                : "Créer un compte pour faire une adhésion"}
            </button>
          </div>
        </Reveal>
      </div>

      <DojoPhotoModal
        open={dojoPhotoOpen}
        dojo={selectedDojo}
        onClose={() => setDojoPhotoOpen(false)}
      />

      <GroupModal
        open={Boolean(selectedGroup)}
        onClose={() => setSelectedGroup(null)}
        group={selectedGroup}
        dojo={dojoForModal}
      />
    </section>
  );
}